/** Full country dial-code dataset for `PhoneNumberField` — every ISO-3166-1
 * country `country-flag-icons` ships a flag for, paired with its E.164
 * calling code. Sri Lanka is pinned first (Sanath Gamage Coaching's client
 * base is overwhelmingly Sri Lankan); everything else is sorted
 * alphabetically by label at render time by the component itself.
 *
 * Several countries share a calling code (e.g. the NANP: US/Canada/Jamaica/
 * ... all dial "1") — each still gets its own entry so a coach can find a
 * client's actual country by name; `PhoneNumberField`'s longest-code-first
 * parser only needs the `dial` value to be correct, not unique. */
export interface DialCodeEntry {
  iso2: string;
  dial: string;
  label: string;
}

export const DEFAULT_DIAL_ISO2 = 'LK';

export const COUNTRY_DIAL_CODES: DialCodeEntry[] = [
  { iso2: 'LK', dial: '94', label: 'Sri Lanka' },

  { iso2: 'AF', dial: '93', label: 'Afghanistan' },
  { iso2: 'AL', dial: '355', label: 'Albania' },
  { iso2: 'DZ', dial: '213', label: 'Algeria' },
  { iso2: 'AD', dial: '376', label: 'Andorra' },
  { iso2: 'AO', dial: '244', label: 'Angola' },
  { iso2: 'AG', dial: '1268', label: 'Antigua and Barbuda' },
  { iso2: 'AR', dial: '54', label: 'Argentina' },
  { iso2: 'AM', dial: '374', label: 'Armenia' },
  { iso2: 'AU', dial: '61', label: 'Australia' },
  { iso2: 'AT', dial: '43', label: 'Austria' },
  { iso2: 'AZ', dial: '994', label: 'Azerbaijan' },
  { iso2: 'BS', dial: '1242', label: 'Bahamas' },
  { iso2: 'BH', dial: '973', label: 'Bahrain' },
  { iso2: 'BD', dial: '880', label: 'Bangladesh' },
  { iso2: 'BB', dial: '1246', label: 'Barbados' },
  { iso2: 'BY', dial: '375', label: 'Belarus' },
  { iso2: 'BE', dial: '32', label: 'Belgium' },
  { iso2: 'BZ', dial: '501', label: 'Belize' },
  { iso2: 'BJ', dial: '229', label: 'Benin' },
  { iso2: 'BT', dial: '975', label: 'Bhutan' },
  { iso2: 'BO', dial: '591', label: 'Bolivia' },
  { iso2: 'BA', dial: '387', label: 'Bosnia and Herzegovina' },
  { iso2: 'BW', dial: '267', label: 'Botswana' },
  { iso2: 'BR', dial: '55', label: 'Brazil' },
  { iso2: 'BN', dial: '673', label: 'Brunei' },
  { iso2: 'BG', dial: '359', label: 'Bulgaria' },
  { iso2: 'BF', dial: '226', label: 'Burkina Faso' },
  { iso2: 'BI', dial: '257', label: 'Burundi' },
  { iso2: 'KH', dial: '855', label: 'Cambodia' },
  { iso2: 'CM', dial: '237', label: 'Cameroon' },
  { iso2: 'CA', dial: '1', label: 'Canada' },
  { iso2: 'CV', dial: '238', label: 'Cape Verde' },
  { iso2: 'CF', dial: '236', label: 'Central African Republic' },
  { iso2: 'TD', dial: '235', label: 'Chad' },
  { iso2: 'CL', dial: '56', label: 'Chile' },
  { iso2: 'CN', dial: '86', label: 'China' },
  { iso2: 'CO', dial: '57', label: 'Colombia' },
  { iso2: 'KM', dial: '269', label: 'Comoros' },
  { iso2: 'CG', dial: '242', label: 'Congo-Brazzaville' },
  { iso2: 'CD', dial: '243', label: 'Congo (DRC)' },
  { iso2: 'CR', dial: '506', label: 'Costa Rica' },
  { iso2: 'CI', dial: '225', label: "Côte d'Ivoire" },
  { iso2: 'HR', dial: '385', label: 'Croatia' },
  { iso2: 'CU', dial: '53', label: 'Cuba' },
  { iso2: 'CY', dial: '357', label: 'Cyprus' },
  { iso2: 'CZ', dial: '420', label: 'Czechia' },
  { iso2: 'DK', dial: '45', label: 'Denmark' },
  { iso2: 'DJ', dial: '253', label: 'Djibouti' },
  { iso2: 'DM', dial: '1767', label: 'Dominica' },
  { iso2: 'DO', dial: '1809', label: 'Dominican Republic' },
  { iso2: 'EC', dial: '593', label: 'Ecuador' },
  { iso2: 'EG', dial: '20', label: 'Egypt' },
  { iso2: 'SV', dial: '503', label: 'El Salvador' },
  { iso2: 'GQ', dial: '240', label: 'Equatorial Guinea' },
  { iso2: 'ER', dial: '291', label: 'Eritrea' },
  { iso2: 'EE', dial: '372', label: 'Estonia' },
  { iso2: 'SZ', dial: '268', label: 'Eswatini' },
  { iso2: 'ET', dial: '251', label: 'Ethiopia' },
  { iso2: 'FJ', dial: '679', label: 'Fiji' },
  { iso2: 'FI', dial: '358', label: 'Finland' },
  { iso2: 'FR', dial: '33', label: 'France' },
  { iso2: 'GA', dial: '241', label: 'Gabon' },
  { iso2: 'GM', dial: '220', label: 'Gambia' },
  { iso2: 'GE', dial: '995', label: 'Georgia' },
  { iso2: 'DE', dial: '49', label: 'Germany' },
  { iso2: 'GH', dial: '233', label: 'Ghana' },
  { iso2: 'GR', dial: '30', label: 'Greece' },
  { iso2: 'GD', dial: '1473', label: 'Grenada' },
  { iso2: 'GT', dial: '502', label: 'Guatemala' },
  { iso2: 'GN', dial: '224', label: 'Guinea' },
  { iso2: 'GW', dial: '245', label: 'Guinea-Bissau' },
  { iso2: 'GY', dial: '592', label: 'Guyana' },
  { iso2: 'HT', dial: '509', label: 'Haiti' },
  { iso2: 'HN', dial: '504', label: 'Honduras' },
  { iso2: 'HK', dial: '852', label: 'Hong Kong' },
  { iso2: 'HU', dial: '36', label: 'Hungary' },
  { iso2: 'IS', dial: '354', label: 'Iceland' },
  { iso2: 'IN', dial: '91', label: 'India' },
  { iso2: 'ID', dial: '62', label: 'Indonesia' },
  { iso2: 'IR', dial: '98', label: 'Iran' },
  { iso2: 'IQ', dial: '964', label: 'Iraq' },
  { iso2: 'IE', dial: '353', label: 'Ireland' },
  { iso2: 'IL', dial: '972', label: 'Israel' },
  { iso2: 'IT', dial: '39', label: 'Italy' },
  { iso2: 'JM', dial: '1876', label: 'Jamaica' },
  { iso2: 'JP', dial: '81', label: 'Japan' },
  { iso2: 'JO', dial: '962', label: 'Jordan' },
  { iso2: 'KZ', dial: '7', label: 'Kazakhstan' },
  { iso2: 'KE', dial: '254', label: 'Kenya' },
  { iso2: 'KI', dial: '686', label: 'Kiribati' },
  { iso2: 'KW', dial: '965', label: 'Kuwait' },
  { iso2: 'KG', dial: '996', label: 'Kyrgyzstan' },
  { iso2: 'LA', dial: '856', label: 'Laos' },
  { iso2: 'LV', dial: '371', label: 'Latvia' },
  { iso2: 'LB', dial: '961', label: 'Lebanon' },
  { iso2: 'LS', dial: '266', label: 'Lesotho' },
  { iso2: 'LR', dial: '231', label: 'Liberia' },
  { iso2: 'LY', dial: '218', label: 'Libya' },
  { iso2: 'LI', dial: '423', label: 'Liechtenstein' },
  { iso2: 'LT', dial: '370', label: 'Lithuania' },
  { iso2: 'LU', dial: '352', label: 'Luxembourg' },
  { iso2: 'MO', dial: '853', label: 'Macao' },
  { iso2: 'MG', dial: '261', label: 'Madagascar' },
  { iso2: 'MW', dial: '265', label: 'Malawi' },
  { iso2: 'MY', dial: '60', label: 'Malaysia' },
  { iso2: 'MV', dial: '960', label: 'Maldives' },
  { iso2: 'ML', dial: '223', label: 'Mali' },
  { iso2: 'MT', dial: '356', label: 'Malta' },
  { iso2: 'MH', dial: '692', label: 'Marshall Islands' },
  { iso2: 'MR', dial: '222', label: 'Mauritania' },
  { iso2: 'MU', dial: '230', label: 'Mauritius' },
  { iso2: 'MX', dial: '52', label: 'Mexico' },
  { iso2: 'FM', dial: '691', label: 'Micronesia' },
  { iso2: 'MD', dial: '373', label: 'Moldova' },
  { iso2: 'MC', dial: '377', label: 'Monaco' },
  { iso2: 'MN', dial: '976', label: 'Mongolia' },
  { iso2: 'ME', dial: '382', label: 'Montenegro' },
  { iso2: 'MA', dial: '212', label: 'Morocco' },
  { iso2: 'MZ', dial: '258', label: 'Mozambique' },
  { iso2: 'MM', dial: '95', label: 'Myanmar' },
  { iso2: 'NA', dial: '264', label: 'Namibia' },
  { iso2: 'NR', dial: '674', label: 'Nauru' },
  { iso2: 'NP', dial: '977', label: 'Nepal' },
  { iso2: 'NL', dial: '31', label: 'Netherlands' },
  { iso2: 'NZ', dial: '64', label: 'New Zealand' },
  { iso2: 'NI', dial: '505', label: 'Nicaragua' },
  { iso2: 'NE', dial: '227', label: 'Niger' },
  { iso2: 'NG', dial: '234', label: 'Nigeria' },
  { iso2: 'KP', dial: '850', label: 'North Korea' },
  { iso2: 'MK', dial: '389', label: 'North Macedonia' },
  { iso2: 'NO', dial: '47', label: 'Norway' },
  { iso2: 'OM', dial: '968', label: 'Oman' },
  { iso2: 'PK', dial: '92', label: 'Pakistan' },
  { iso2: 'PW', dial: '680', label: 'Palau' },
  { iso2: 'PS', dial: '970', label: 'Palestine' },
  { iso2: 'PA', dial: '507', label: 'Panama' },
  { iso2: 'PG', dial: '675', label: 'Papua New Guinea' },
  { iso2: 'PY', dial: '595', label: 'Paraguay' },
  { iso2: 'PE', dial: '51', label: 'Peru' },
  { iso2: 'PH', dial: '63', label: 'Philippines' },
  { iso2: 'PL', dial: '48', label: 'Poland' },
  { iso2: 'PT', dial: '351', label: 'Portugal' },
  { iso2: 'QA', dial: '974', label: 'Qatar' },
  { iso2: 'RO', dial: '40', label: 'Romania' },
  { iso2: 'RU', dial: '7', label: 'Russia' },
  { iso2: 'RW', dial: '250', label: 'Rwanda' },
  { iso2: 'KN', dial: '1869', label: 'Saint Kitts and Nevis' },
  { iso2: 'LC', dial: '1758', label: 'Saint Lucia' },
  { iso2: 'VC', dial: '1784', label: 'Saint Vincent and the Grenadines' },
  { iso2: 'WS', dial: '685', label: 'Samoa' },
  { iso2: 'SM', dial: '378', label: 'San Marino' },
  { iso2: 'ST', dial: '239', label: 'São Tomé and Príncipe' },
  { iso2: 'SA', dial: '966', label: 'Saudi Arabia' },
  { iso2: 'SN', dial: '221', label: 'Senegal' },
  { iso2: 'RS', dial: '381', label: 'Serbia' },
  { iso2: 'SC', dial: '248', label: 'Seychelles' },
  { iso2: 'SL', dial: '232', label: 'Sierra Leone' },
  { iso2: 'SG', dial: '65', label: 'Singapore' },
  { iso2: 'SK', dial: '421', label: 'Slovakia' },
  { iso2: 'SI', dial: '386', label: 'Slovenia' },
  { iso2: 'SB', dial: '677', label: 'Solomon Islands' },
  { iso2: 'SO', dial: '252', label: 'Somalia' },
  { iso2: 'ZA', dial: '27', label: 'South Africa' },
  { iso2: 'KR', dial: '82', label: 'South Korea' },
  { iso2: 'SS', dial: '211', label: 'South Sudan' },
  { iso2: 'ES', dial: '34', label: 'Spain' },
  { iso2: 'SD', dial: '249', label: 'Sudan' },
  { iso2: 'SR', dial: '597', label: 'Suriname' },
  { iso2: 'SE', dial: '46', label: 'Sweden' },
  { iso2: 'CH', dial: '41', label: 'Switzerland' },
  { iso2: 'SY', dial: '963', label: 'Syria' },
  { iso2: 'TW', dial: '886', label: 'Taiwan' },
  { iso2: 'TJ', dial: '992', label: 'Tajikistan' },
  { iso2: 'TZ', dial: '255', label: 'Tanzania' },
  { iso2: 'TH', dial: '66', label: 'Thailand' },
  { iso2: 'TL', dial: '670', label: 'Timor-Leste' },
  { iso2: 'TG', dial: '228', label: 'Togo' },
  { iso2: 'TO', dial: '676', label: 'Tonga' },
  { iso2: 'TT', dial: '1868', label: 'Trinidad and Tobago' },
  { iso2: 'TN', dial: '216', label: 'Tunisia' },
  { iso2: 'TR', dial: '90', label: 'Turkey' },
  { iso2: 'TM', dial: '993', label: 'Turkmenistan' },
  { iso2: 'TV', dial: '688', label: 'Tuvalu' },
  { iso2: 'UG', dial: '256', label: 'Uganda' },
  { iso2: 'UA', dial: '380', label: 'Ukraine' },
  { iso2: 'AE', dial: '971', label: 'United Arab Emirates' },
  { iso2: 'GB', dial: '44', label: 'United Kingdom' },
  { iso2: 'US', dial: '1', label: 'United States' },
  { iso2: 'UY', dial: '598', label: 'Uruguay' },
  { iso2: 'UZ', dial: '998', label: 'Uzbekistan' },
  { iso2: 'VU', dial: '678', label: 'Vanuatu' },
  { iso2: 'VA', dial: '379', label: 'Vatican City' },
  { iso2: 'VE', dial: '58', label: 'Venezuela' },
  { iso2: 'VN', dial: '84', label: 'Vietnam' },
  { iso2: 'YE', dial: '967', label: 'Yemen' },
  { iso2: 'ZM', dial: '260', label: 'Zambia' },
  { iso2: 'ZW', dial: '263', label: 'Zimbabwe' },
];
